// Progress types and pure helpers shared by the browser and the server.

import { dayIn, shiftDay } from '@/lib/time-zone'

export interface LessonRecord {
    status: 'started' | 'completed'
    best: number
    completedAt?: string
}

export interface MasteryRecord {
    ref: string
    skill: string
    state: 'new' | 'learning' | 'needs_practice' | 'strong' | 'mastered'
    correct: number
    wrong: number
    dueAt: string | null
    label?: string
}

export interface ErrorTally {
    code: string
    count: number
}

export interface ProgressSnapshot {
    lessons: Record<string, LessonRecord>
    xpTotal: number
    xpToday: number
    days: string[]
    streak: number
    level: number
    levelFloor: number
    levelNext: number
    mastery: MasteryRecord[]
    errors: ErrorTally[]
    badges: string[]
}

/** Level n starts at 25·(n−1)·n XP: 0, 50, 150, 300, 500 … */
export const levelFromXp = (xp: number): { level: number, floor: number, next: number } => {
    let level = 1

    while (25 * level * (level + 1) <= xp) level += 1

    return { level, floor: 25 * (level - 1) * level, next: 25 * level * (level + 1) }
}

/**
 * Consecutive active days ending today (or yesterday: a streak survives until the day ends). Days are YYYY-MM-DD in
 * the learner's own time zone, and `today` is today's date in that same zone (dayIn from @/lib/time-zone).
 */
export const streakFromDays = (days: string[], today: string = dayIn()): number => {
    const set = new Set(days)
    let cursor = set.has(today) ? today : shiftDay(today, -1)
    let streak = 0

    while (set.has(cursor)) {
        streak += 1
        cursor = shiftDay(cursor, -1)
    }

    return streak
}

export const MASTERY_LABEL: Record<MasteryRecord['state'], { hi: string, en: string }> = {
    new: { hi: 'नया', en: 'new' },
    learning: { hi: 'सीख रहे', en: 'learning' },
    needs_practice: { hi: 'अभ्यास चाहिए', en: 'needs practice' },
    strong: { hi: 'मज़बूत', en: 'strong' },
    mastered: { hi: 'पक्का', en: 'mastered' }
}

/** The skills tracked for mastery (docs/00-MASTER-PROMPT.md §3.12). */
export const SKILLS: Record<string, { hi: string, en: string }> = {
    varnamala: { hi: 'वर्णमाला', en: 'Alphabet' },
    shabd: { hi: 'शब्द-भंडार', en: 'Vocabulary' },
    padhna: { hi: 'पढ़ना', en: 'Reading' },
    sunna: { hi: 'सुनना', en: 'Listening' },
    bolna: { hi: 'बोलना', en: 'Speaking' },
    kriya_pahchan: { hi: 'क्रियारूप-पहचान', en: 'Recognising verb forms' },
    kriya_nirman: { hi: 'क्रियारूप-निर्माण', en: 'Building verb forms' },
    kaal_pahchan: { hi: 'काल-पहचान', en: 'Recognising tenses' },
    vibhakti: { hi: 'विभक्ति', en: 'Cases' },
    vakya: { hi: 'वाक्य-रचना', en: 'Sentence building' },
    sandhi: { hi: 'सन्धि', en: 'Sandhi' },
    kridanta: { hi: 'कृदन्त', en: 'Participles' },
    bodh: { hi: 'बोध', en: 'Comprehension' }
}

/** Rule-based weakness detection: which drill helps with a recurring error. */
export const WEAKNESS: Record<string, { hi: string, en: string, href: string }> = {
    agreement_number: { hi: 'कर्ता और क्रिया का वचन मिलाइए (एकवचन/बहुवचन)', en: 'Match the number of subject and verb (singular/plural)', href: '/practice/vachan-badlo' },
    agreement_person: { hi: 'कर्ता और क्रिया का पुरुष मिलाइए (उत्तम/मध्यम/अन्य)', en: 'Match the person of subject and verb', href: '/practice/dhatu-chunauti' },
    wrong_case: { hi: 'कर्म के लिए द्वितीया विभक्ति', en: 'Use द्वितीया for the object', href: '/practice/vibhakti-chayan' },
    wrong_tense: { hi: 'काल की पहचान', en: 'Recognising the tense', href: '/practice/kaal-pahchan' },
    wrong_form: { hi: 'रूप-तालिका पूरी कीजिए', en: 'Complete the form table', href: '/practice/roop-sadhana' },
    wrong_kridanta: { hi: 'कृदन्त की पहचान', en: 'Recognising the participle', href: '/practice/kridanta-pahchan' }
}
